<template>
  <div class="vehicleApps">
    <div
      v-if="loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div v-if="!loading">
      <v-row>
        <v-col
          cols="12"
          sm="8"
          class="pt-0"
        >
          <h1>
            {{ $t(labels.title) }}
          </h1>
        </v-col>
        <v-col
          cols="12"
          sm="4"
          class="pt-0"
        >
          <div class="totals float-sm-right pl-8 pl-sm-0">
            <div
              v-if="monthlyTotalCost > 0 || yearlyTotalCost > 0"
              class="label"
            >
              {{ $t('services_overview.txt.total_cost') }}
            </div>
            <div>
              <span
                v-if="monthlyTotalCost > 0"
                class="total"
              >{{ totalMonthPrice | formatPrice(currency.symbol) }}
              </span>
              <span v-if="monthlyTotalCost > 0">{{
                $t('services_overview.txt.month')
              }}</span>
              <span v-if="monthlyTotalCost > 0 && yearlyTotalCost > 0">
                &
              </span>
              <span
                v-if="yearlyTotalCost > 0"
                class="total"
              >{{ totalAnnualPrice | formatPrice(currency.symbol) }}
              </span>
              <span v-if="yearlyTotalCost > 0">{{
                $t('services_overview.txt.year')
              }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="pl-3">
        <v-col
          cols="10"
          sm="6"
          class="ma-0 pa-0"
        >
          <v-text-field
            v-model="freeSearch"
            :placeholder="$t('vehicleApps_overview.txt.search_box_placeholder')"
            outlined
            dense
            filled
            class="searchBox"
          />
        </v-col>
        <v-col
          cols="1"
          sm="1"
          class="ma-0 pa-0"
        >
          <v-btn
            :disabled="true"
            class="add-athlete rounded-0"
          >
            <v-icon
              size="28"
              color="white"
            >
              mdi-magnify
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="!!disclaimer">
        <v-col>
          <span
            class="disclaimer"
            v-html="disclaimer"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="!filteredApps || filteredApps.length == 0">
          <div class="no-results">
            <v-icon v-if="isThirdPartyApp">
              mdi-checkbox-blank-badge
            </v-icon>
            <v-icon v-else>
              mdi-toy-brick
            </v-icon>
            <br>{{ $t('vehicleApps_overview.txt.empty_search') }}
          </div>
        </v-col>
        <v-col
          v-for="vehicleApp in filteredApps"
          v-else
          :key="vehicleApp.id"
          cols="12"
          md="6"
          sm="12"
        >
          <VehicleAppsCard
            :service="vehicleApp"
            :in-vehicle-app-catalog="vehicleAppList"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import prismicService from '@/helpers/prismicService'
import { mapState, mapActions } from 'vuex'
import VehicleAppsCard from '@/components/VehicleApps/VehicleAppsCard.vue'
import { ServicesMixin } from '@/mixins/ServicesMixin.js'
import { ProductType } from '@/helpers/enums'

import vhlAppService from '@/helpers/vehicleAppService'

export default {
  name: 'VehicleAppsOverview',
  components: {
    VehicleAppsCard
  },
  mixins: [ServicesMixin],
  props: {
    vehicleAppType: {
      type: String,
      default: () => ProductType.InVehicleApp
    }
  },
  data() {
    return {
      vehicleAppList: [],
      loading: false,
      freeSearch: '',
      filteredResult: [],
      disclaimer: ''
    }
  },
  computed: {
    ...mapState('account', ['currency']),
    ...mapState('trucks', ['trucks']),
    filteredApps() {
      const clearSearchTerm = `${this.freeSearch}`.trim().removeDiacritics()
      if (!clearSearchTerm) return this.vehicleAppList

      const searchRx = new RegExp(RegExp.escape(clearSearchTerm), 'i')
      return this.vehicleAppList.filter(vehicle =>
        searchRx.test(vehicle.searchKeywords)
      )
    },
    isThirdPartyApp() {
      return vhlAppService.isThirdPartyType(this.vehicleAppType)
    },
    labels() {
      return {
        title: this.isThirdPartyApp
          ? 'vehicleApps_overview.txt.third_party_apps'
          : 'vehicleApps_overview.txt.vehicle_apps'
      }
    }
  },
  watch: {
    vehicleAppType() {
      this.getVehicleApps()
    }
  },
  async created() {
    await this.getVehicleApps()

    this.disclaimer = this.$t('vehicleApps_overview.txt.disclaimer')
  },
  methods: {
    ...mapActions('trucks', ['getTrucksPerService']),
    ...mapActions('products', ['getCompanyService']),
    getVehicleApps() {
      this.loading = true

      const productAppsEndpoint = this.isThirdPartyApp
        ? api.ProductsGetThirdPartyApps
        : api.ProductsGetApps

      const getProductsTask = apiCalls.get(productAppsEndpoint, '', '')
      const getPrismicTask = prismicService.getProductCatalog()

      Promise.all([this.getTrucksPerService(), this.getCompanyService()]).then(
        ([servicesList, companyService]) => {
          this.activeServices = servicesList || []
          this.calculateTotals(true)

          this.addCompanyTotals(companyService)
        }
      )

      return Promise.all([getProductsTask, getPrismicTask]).then(
        ([{ data: appList }, catalog]) => {
          this.vehicleAppList = this.mapPrismicInfo(appList, catalog)

          this.vehicleAppList = this.vehicleAppList.filter(item =>
            item.productRatePlans.some(
              x => x.isAllowedToSubscribe__c === 'True'
            )
          )
          this.vehicleAppList.sort(this.sortAppList)
          this.loading = false
        }
      )
    },
    showVehicleAppsCard(vehicleApp) {
      return vehicleApp.productRatePlans.some(
        item => item.isAllowedToSubscribe__c === 'True'
      )
    },
    mapPrismicInfo(appList, catalog) {
      if (!catalog || catalog.length == 0) return

      appList.forEach(vehicleApp => {
        const catalogProduct = catalog.find(
          catalogProduct => catalogProduct.externalKey === vehicleApp.sku
        )

        if (catalogProduct) {
          vehicleApp.name = catalogProduct.title
          vehicleApp.iconUrl = catalogProduct.iconActiveUrl
          vehicleApp.description = catalogProduct.description
          vehicleApp.title = catalogProduct.descriptionShort
          vehicleApp.searchKeywords = `${vehicleApp.name} ${vehicleApp.description}`.removeDiacritics()
        }
      })

      return appList
    },
    sortAppList(app1, app2) {
      if (app1.name < app2.name) return -1
      if (app1.name > app2.name) return 1
      return 0
    }
  }
}
</script>

<style lang="scss">
.vehicleApps {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .totals {
    .label {
      font-size: 0.75rem;
      font-weight: bold;
      text-transform: uppercase;
      color: var(--v-bluegrey-base);
    }
    .total {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--v-slate-base);
      span {
        font-size: 0.875rem;
      }
    }
  }
  .add-athlete {
    height: 40px !important;
    width: 100%;
    background-image: linear-gradient(to bottom, #266cab 0%, #01529c);
    border-radius: 0;
    box-shadow: none;

    &:disabled .v-icon {
      color: white !important;
    }
  }

  .searchBox fieldset {
    background: white;
    border-radius: 0;
    top: -4px;
  }

  .disclaimer {
    p {
      margin-bottom: 10px;
      text-align: justify;
    }
  }
}
</style>
